import { PUBLIC_PENDING_ABSENCE_TYPES } from "@/lib/employeeAbsences";
import type { PlanungAbsence } from "@/lib/montageplanung";
import { enumerateDays, parseIso, toIso } from "./dateUtils";

export const VACATION_CREDITED_COLOR = "#8e8e93";

type HalfPart = "am" | "pm";

function coverageParts(abs: PlanungAbsence, dayIso: string): Set<HalfPart> {
  const from = abs.date_from.slice(0, 10);
  const to = abs.date_to.slice(0, 10);
  if (dayIso < from || dayIso > to) return new Set();
  if (from === to && Number(abs.day_fraction) === 0.5) {
    return new Set([abs.half_day_part === "am" ? "am" : "pm"]);
  }
  return new Set(["am", "pm"]);
}

function vacationDayCredited(
  vacation: PlanungAbsence,
  dayIso: string,
  sickAbsences: PlanungAbsence[],
): boolean {
  const vacParts = coverageParts(vacation, dayIso);
  if (vacParts.size === 0) return false;
  for (const sick of sickAbsences) {
    const sickParts = coverageParts(sick, dayIso);
    for (const part of vacParts) {
      if (sickParts.has(part)) return true;
    }
  }
  return false;
}

function splitVacationBySickness(
  vacation: PlanungAbsence,
  sickAbsences: PlanungAbsence[],
): PlanungAbsence[] {
  if (sickAbsences.length === 0) return [vacation];

  const days = enumerateDays(parseIso(vacation.date_from), parseIso(vacation.date_to)).map(
    (day) => {
      const iso = toIso(day);
      return { iso, credited: vacationDayCredited(vacation, iso, sickAbsences) };
    },
  );
  if (!days.some((day) => day.credited)) return [vacation];

  const segments: PlanungAbsence[] = [];
  let index = 0;
  while (index < days.length) {
    const credited = days[index]!.credited;
    let end = index + 1;
    while (end < days.length && days[end]!.credited === credited) end += 1;
    const from = days[index]!.iso;
    const to = days[end - 1]!.iso;
    const singleHalfDay =
      vacation.date_from.slice(0, 10) === vacation.date_to.slice(0, 10) &&
      Number(vacation.day_fraction) === 0.5;
    segments.push({
      ...vacation,
      date_from: from,
      date_to: to,
      day_fraction: singleHalfDay ? vacation.day_fraction : 1,
      half_day_part: singleHalfDay ? vacation.half_day_part : null,
      vacation_credited: credited,
    });
    index = end;
  }
  return segments;
}

export function isSicknessAbsenceType(type: PlanungAbsence["absence_type"]): boolean {
  return PUBLIC_PENDING_ABSENCE_TYPES.has(type);
}

export function expandAbsencesForVacationSickness(
  absences: PlanungAbsence[],
): PlanungAbsence[] {
  const sickByLogin = new Map<string, PlanungAbsence[]>();
  for (const abs of absences) {
    if (!isSicknessAbsenceType(abs.absence_type)) continue;
    const login = abs.user_login.toLowerCase();
    const list = sickByLogin.get(login) ?? [];
    list.push(abs);
    sickByLogin.set(login, list);
  }

  const out: PlanungAbsence[] = [];
  for (const abs of absences) {
    if (abs.absence_type !== "urlaub") {
      out.push(abs);
      continue;
    }
    out.push(
      ...splitVacationBySickness(
        abs,
        sickByLogin.get(abs.user_login.toLowerCase()) ?? [],
      ),
    );
  }
  return out;
}

export function absenceLayoutKey(abs: PlanungAbsence): string {
  const credited = abs.vacation_credited ? "g" : "v";
  return `${abs.id}:${abs.date_from}:${abs.date_to}:${credited}:${abs.half_day_part ?? ""}`;
}
